import { Link, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { getRelatedArticles, getScienceArticleBySlug, SCIENCE_ARTICLES } from '../constants/scienceContent';
import { ScienceArticleBody } from '../components/science/ScienceArticleBody';
import { THERMACUBE_CHECKOUT_URL } from '../constants/storeUrls';
import { THERMACUBE_PRICE_INR } from '../constants/thermaCube';

const EASE = [0.22, 1, 0.36, 1];

function formatInr(n) {
  return `\u20B9${Math.round(n).toLocaleString('en-IN')}`;
}

export function ScienceArticle() {
  const { slug } = useParams();
  const article = slug ? getScienceArticleBySlug(slug) : null;
  const reduceMotion = useReducedMotion();
  const t = (base) => (reduceMotion ? { duration: 0 } : { ...base });
  const related = article ? getRelatedArticles(article.slug) : [];
  const showMuscleDiagram = article?.slug === 'muscle-soreness-101';

  if (!article) {
    return (
      <div className="min-h-screen pt-[132px] pb-24 px-5 flex flex-col items-center justify-center bg-[#06080a] text-center">
        <h1 className="font-heading text-2xl text-white mb-4">Article not found</h1>
        <p className="text-zinc-500 mb-8 max-w-md">This guide may have moved. Browse all science pieces from the hub.</p>
        <Link
          to="/science"
          className="inline-flex items-center gap-2 text-teal-400 font-mono text-xs uppercase tracking-[0.2em] hover:underline"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden />
          Back to science hub
        </Link>
      </div>
    );
  }

  return (
    <article className="min-h-screen pt-[132px] pb-24 bg-[#06080a] text-zinc-100">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_70%_40%_at_50%_0%,rgba(44,95,84,0.12),transparent_50%)]" aria-hidden />

      <header className="relative border-b border-white/[0.06]">
        <div className="relative h-[min(44vh,460px)] w-full overflow-hidden">
          <img
            src={article.heroImage}
            alt={article.heroImageAlt}
            className="absolute inset-0 w-full h-full object-cover object-center"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#06080a] via-[#06080a]/78 to-[#06080a]/25" aria-hidden />
        </div>
        <div className="relative z-[1] max-w-[760px] mx-auto px-5 sm:px-8 -mt-16 md:-mt-20 pb-12 md:pb-16">
          <Link
            to="/science"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hover:text-teal-400 transition-colors mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" aria-hidden />
            Science hub
          </Link>
          <motion.div
            initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={t({ duration: 0.75, ease: EASE })}
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-teal-400/85 mb-4">
              {article.category} · {article.readTime} · {article.publishedAt}
            </p>
            <h1 className="font-heading text-[clamp(1.85rem,4.2vw,3rem)] font-semibold tracking-[-0.03em] leading-[1.05] text-white mb-6">
              {article.title}
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl leading-relaxed">{article.dek}</p>
          </motion.div>
        </div>
      </header>

      <div className="relative max-w-[760px] mx-auto px-5 sm:px-8 py-14 md:py-20">
        <ScienceArticleBody blocks={article.blocks ?? []} showMuscleDiagramAfterH2={showMuscleDiagram} />

        <div className="mt-16 md:mt-20 rounded-2xl border border-white/[0.1] bg-gradient-to-br from-zinc-900/80 to-black/80 p-8 md:p-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-teal-400/80 mb-3">Hardware</p>
          <h2 className="font-heading text-xl md:text-2xl text-white mb-3">Put the protocol in your drawer</h2>
          <p className="text-zinc-400 text-sm leading-relaxed mb-6 max-w-lg">
            ThermaCube delivers heat, cold, and contrast in one cordless pad—built for the kind of sessions these articles describe.
          </p>
          <a
            href={THERMACUBE_CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-neutral-950 text-[11px] font-semibold uppercase tracking-[0.16em] hover:bg-[#c5ebe2] transition-colors"
          >
            Shop ThermaCube · {formatInr(THERMACUBE_PRICE_INR)}
            <ArrowUpRight className="w-4 h-4" aria-hidden />
          </a>
        </div>

        {related.length > 0 ? (
          <section className="mt-20 md:mt-24">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-6">Related</h2>
            <ul className="space-y-4">
              {related.filter(Boolean).map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/science/${r.slug}`}
                    className="group flex items-center justify-between gap-4 py-4 border-b border-white/[0.06] text-zinc-300 hover:text-white transition-colors"
                  >
                    <span className="font-heading text-lg tracking-tight">{r.title}</span>
                    <ArrowUpRight className="w-4 h-4 text-teal-500/70 shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <div className="mt-16 pt-10 border-t border-white/[0.06]">
          <Link
            to="/science"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-teal-400/90 hover:text-teal-300"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden />
            All articles ({SCIENCE_ARTICLES.length})
          </Link>
        </div>
      </div>
    </article>
  );
}
